import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { clearTokenInfo } from '../../utils/storageUtils';
import type { User } from '../../services/types';
import { resetReduxState } from '../actions';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  verificationToken: string | null;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isAdmin: false,
  verificationToken: null,
};

/**
 * Check if user is an admin based on userType
 * Admin types: internalAdministrators, externalAdministrators, superAdministrators
 */
const checkIsAdmin = (userType: string | undefined): boolean => {
  return (
    userType === 'internalAdministrators' ||
    userType === 'superAdministrators' ||
    userType === 'externalAdministrators'
  );
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuth: (state, action: PayloadAction<{ user: User }>) => {
      state.user = action.payload.user;
      state.isAuthenticated = true;
      state.isAdmin = checkIsAdmin(action.payload.user?.userType);
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    setVerificationToken: (state, action: PayloadAction<string | null>) => {
      state.verificationToken = action.payload;
    },
    clearAuth: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isAdmin = false;
      state.verificationToken = null;
      
      clearTokenInfo();
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetReduxState, (state) => {
      // Reset to initial state
      state.user = null;
      state.isAuthenticated = false;
      state.isAdmin = false;
      state.verificationToken = null;
      
      // Also clear storage
      clearTokenInfo();
    });
  },
});

export const { setAuth, updateUser, setVerificationToken, clearAuth } = authSlice.actions;
export default authSlice.reducer; 